<template>
  <div class="ln-page">
    <div class="ln-wrap">
      <header class="tm-head">
        <p class="ln-eyebrow">Dashboard</p>
        <h1 class="tm-title">Team</h1>
        <p class="tm-lede">
          Invite your team to collaborate on your links, domains and deep
          linking apps.
        </p>
      </header>

      <!-- the defaults below are not the team's data: shown as facts they would read
           "Name your team" and 0 of 1 seats, and a saved name would overwrite the real one -->
      <section v-if="!loaded" class="tm-card" data-testid="team-load-failed">
        <p class="tm-text">
          The team could not be loaded. Reload the page to try again.
        </p>
      </section>

      <div v-else class="tm-layout">
        <aside class="tm-side">
          <!-- name and seats -->
          <section class="tm-card">
            <div class="tm-name-row">
              <h2 class="tm-name">{{ team.name || "Name your team" }}</h2>
              <v-tooltip
                v-if="isAdmin && !needsName && !editingName"
                location="bottom"
              >
                <template v-slot:activator="{ props }">
                  <button
                    type="button"
                    class="ln-iconbtn"
                    aria-label="Rename the team"
                    v-bind="props"
                    @click="editingName = true"
                  >
                    <v-icon size="small">mdi-pencil</v-icon>
                  </button>
                </template>
                <span>Rename the team</span>
              </v-tooltip>
            </div>

            <form
              v-if="isAdmin && (needsName || editingName)"
              class="tm-form"
              @submit.prevent="saveName"
            >
              <p v-if="needsName" class="tm-text">
                The name is shown to the people you invite.
              </p>
              <div class="ln-field">
                <v-text-field
                  v-model="nameInput"
                  label="Team name"
                  variant="outlined"
                  density="compact"
                  maxlength="64"
                  hide-details
                  data-testid="team-name-input"
                />
              </div>
              <div class="tm-actions">
                <button
                  v-if="editingName"
                  type="button"
                  class="ln-btn ln-btn--quiet"
                  @click="editingName = false"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  class="ln-btn ln-btn--solid"
                  :disabled="busyId === 'name' || !nameInput.trim()"
                >
                  <v-icon size="small" class="ln-btn__icon"
                    >mdi-content-save</v-icon
                  >
                  Save
                </button>
              </div>
            </form>
            <p v-else-if="needsName" class="tm-text">
              An admin needs to name the team before anyone can be invited.
            </p>

            <div class="tm-seats">
              <span class="tm-seats__label" data-testid="team-seats">{{
                seatsLabel
              }}</span>
              <div
                v-if="seats.max !== -1"
                class="tm-meter"
                :class="{ 'tm-meter--full': seatsFull }"
                aria-hidden="true"
              >
                <div
                  class="tm-meter__fill"
                  :style="{ transform: `scaleX(${seatsFraction})` }"
                ></div>
              </div>
              <p class="tm-note">
                Everyone on the team and every pending invitation count toward
                your plan’s seats.
              </p>
            </div>
          </section>

          <!-- Free: what a paid plan adds -->
          <section v-if="onFreePlan" class="tm-card" data-testid="team-upsell">
            <h2 class="tm-card__title">Invite your team</h2>
            <p class="tm-text">
              Collaborate with your team on 1LINK.IO. Startup includes 3 seats,
              Pro 10 and Enterprise unlimited.
            </p>
            <div class="tm-actions">
              <router-link to="/plan" class="ln-btn ln-btn--solid">
                See plans
                <v-icon size="small" class="ln-btn__icon ln-btn__icon--right"
                  >mdi-arrow-right</v-icon
                >
              </router-link>
            </div>
          </section>

          <!-- invite -->
          <section v-else-if="canWrite && !needsName" class="tm-card">
            <h2 class="tm-card__title">Invite someone</h2>
            <p v-if="seatsFull" class="tm-text">
              Your team uses all its seats.
              <router-link to="/plan" class="ln-inline-link"
                >Upgrade your plan</router-link
              >
              to invite more people.
            </p>
            <form v-else class="tm-form" @submit.prevent="invite">
              <div class="ln-field">
                <v-text-field
                  v-model="inviteEmail"
                  label="Email address"
                  type="email"
                  autocomplete="off"
                  variant="outlined"
                  density="compact"
                  hide-details
                  data-testid="invite-email"
                />
              </div>
              <div class="ln-field">
                <v-select
                  v-model="inviteRole"
                  :items="inviteRoles"
                  label="Role"
                  variant="outlined"
                  density="compact"
                  hide-details
                  data-testid="invite-role"
                />
              </div>
              <div class="tm-actions">
                <button
                  type="submit"
                  class="ln-btn ln-btn--solid"
                  :disabled="busyId === 'invite'"
                >
                  <v-icon size="small" class="ln-btn__icon"
                    >mdi-send-outline</v-icon
                  >
                  Send invitation
                </button>
              </div>
            </form>
          </section>
        </aside>

        <div class="tm-main">
          <!-- members -->
          <section class="tm-card tm-card--flush">
            <div class="tm-card__head">
              <h2 class="tm-card__title">Members</h2>
              <span class="tm-count">{{ members.length }}</span>
            </div>
            <ul class="tm-list">
              <li
                v-for="member in members"
                :key="member.userId"
                class="tm-row"
                data-testid="team-member"
              >
                <span class="tm-avatar" aria-hidden="true">{{
                  initials(member)
                }}</span>
                <div class="tm-row__text">
                  <div class="tm-row__name">
                    {{ member.name || member.email || "Unknown member" }}
                    <span v-if="isSelf(member)" class="tm-tag">You</span>
                    <span v-if="member.accountHolder" class="tm-tag"
                      >Owner</span
                    >
                  </div>
                  <div v-if="member.email && member.name" class="tm-row__sub">
                    {{ member.email }}
                  </div>
                  <div class="tm-row__sub">
                    Joined {{ formatDate(member.joined_at) }}
                  </div>
                </div>
                <div class="tm-row__actions">
                  <v-menu v-if="canChangeRole(member)" location="bottom end">
                    <template v-slot:activator="{ props }">
                      <button
                        type="button"
                        class="ln-btn ln-btn--quiet ln-btn--sm"
                        :aria-label="`Role: ${roleLabel(member.role)}`"
                        :disabled="busyId === member.userId"
                        v-bind="props"
                        data-testid="member-role"
                      >
                        {{ roleLabel(member.role) }}
                        <v-icon size="x-small" class="ln-btn__icon"
                          >mdi-menu-down</v-icon
                        >
                      </button>
                    </template>
                    <div class="ln-menu" role="menu">
                      <button
                        v-for="option in roleOptions"
                        :key="option.value"
                        type="button"
                        class="ln-menu__item"
                        role="menuitemradio"
                        :aria-checked="option.value === member.role"
                        @click="
                          option.value !== member.role &&
                          changeRole(member, option.value)
                        "
                      >
                        <v-icon size="small">{{
                          option.value === member.role
                            ? "mdi-check"
                            : "mdi-blank"
                        }}</v-icon>
                        {{ option.title }}
                      </button>
                    </div>
                  </v-menu>
                  <span v-else class="tm-role">{{
                    roleLabel(member.role)
                  }}</span>
                  <v-tooltip v-if="canRemove(member)" location="bottom">
                    <template v-slot:activator="{ props }">
                      <button
                        type="button"
                        class="ln-iconbtn ln-iconbtn--danger"
                        aria-label="Remove from the team"
                        :disabled="busyId === member.userId"
                        v-bind="props"
                        @click="remove(member)"
                      >
                        <v-icon size="small">mdi-delete</v-icon>
                      </button>
                    </template>
                    <span>Remove from the team</span>
                  </v-tooltip>
                  <button
                    v-if="canLeave(member)"
                    type="button"
                    class="ln-btn ln-btn--quiet ln-btn--sm"
                    @click="leave(member)"
                  >
                    <v-icon size="x-small" class="ln-btn__icon"
                      >mdi-logout</v-icon
                    >
                    Leave team
                  </button>
                </div>
              </li>
            </ul>
            <p
              v-if="isAdmin && members.length > 1"
              class="tm-note tm-note--pad"
            >
              API keys stay active when someone leaves the team. If they had
              access to a key, replace it in
              <router-link to="/integrations" class="ln-inline-link"
                >Integrations</router-link
              >.
            </p>
          </section>

          <!-- invitations -->
          <section v-if="invitations.length" class="tm-card tm-card--flush">
            <div class="tm-card__head">
              <h2 class="tm-card__title">Pending invitations</h2>
              <span class="tm-count">{{ invitations.length }}</span>
            </div>
            <ul class="tm-list">
              <li
                v-for="invitation in invitations"
                :key="invitation.id"
                class="tm-row"
                data-testid="team-invitation"
              >
                <span class="tm-avatar tm-avatar--pending" aria-hidden="true"
                  ><v-icon size="small">mdi-email-outline</v-icon></span
                >
                <div class="tm-row__text">
                  <div class="tm-row__name">
                    {{ invitation.email }}
                    <span v-if="invitation.expired" class="tm-tag tm-tag--warn"
                      >Expired</span
                    >
                  </div>
                  <div class="tm-row__sub">
                    {{ roleLabel(invitation.role) }}
                  </div>
                  <div v-if="!invitation.expired" class="tm-row__sub">
                    Expires {{ formatDate(invitation.expires_at) }}
                  </div>
                </div>
                <!-- same rule as revoke: the API refuses a member resending
                     someone else's invitation (spec §5) -->
                <div v-if="canRevoke(invitation)" class="tm-row__actions">
                  <v-tooltip location="bottom">
                    <template v-slot:activator="{ props }">
                      <button
                        type="button"
                        class="ln-iconbtn"
                        aria-label="Send the invitation again"
                        :disabled="busyId === invitation.id"
                        v-bind="props"
                        @click="resend(invitation)"
                      >
                        <v-icon size="small">mdi-email-sync-outline</v-icon>
                      </button>
                    </template>
                    <span>Send again</span>
                  </v-tooltip>
                  <v-tooltip location="bottom">
                    <template v-slot:activator="{ props }">
                      <button
                        type="button"
                        class="ln-iconbtn ln-iconbtn--danger"
                        aria-label="Revoke the invitation"
                        :disabled="busyId === invitation.id"
                        v-bind="props"
                        @click="revoke(invitation)"
                      >
                        <v-icon size="small">mdi-delete</v-icon>
                      </button>
                    </template>
                    <span>Revoke the invitation</span>
                  </v-tooltip>
                </div>
              </li>
            </ul>
          </section>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { mapGetters, mapState } from "vuex";
import {
  changeMemberRole,
  getTeamMembers,
  handleApiError,
  inviteToTeam,
  removeMember,
  renameTeam,
  revokeInvitation,
} from "../api";
import { ROLE_LABELS, switchWorkspace } from "../workspace";

const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

export default {
  name: "Team",
  data() {
    return {
      team: { id: null, name: null },
      seats: { used: 0, max: 1 },
      members: [],
      invitations: [],
      /** false until the members were fetched once: the defaults above are not the team's */
      loaded: false,
      nameInput: "",
      editingName: false,
      inviteEmail: "",
      inviteRole: "member",
      /** the row or form an action is running on */
      busyId: null,
    };
  },
  computed: {
    ...mapState(["user"]),
    ...mapGetters(["canWrite", "isAdmin"]),
    /** an admin invites any role, a member a member or a viewer */
    inviteRoles() {
      const roles = this.isAdmin
        ? ["admin", "member", "viewer"]
        : ["member", "viewer"];
      return roles.map((value) => ({ value, title: ROLE_LABELS[value] }));
    },
    roleOptions() {
      return ["admin", "member", "viewer"].map((value) => ({
        value,
        title: ROLE_LABELS[value],
      }));
    },
    seatsFull() {
      return this.seats.max !== -1 && this.seats.used >= this.seats.max;
    },
    /** how full the seat meter is, 0 to 1 */
    seatsFraction() {
      return Math.min(this.seats.used / this.seats.max, 1);
    },
    seatsLabel() {
      return this.seats.max === -1
        ? `${this.seats.used} seats, no limit`
        : `${this.seats.used} of ${this.seats.max} seats`;
    },
    onFreePlan() {
      return this.seats.max === 1;
    },
    needsName() {
      return !this.team.name;
    },
    canInvite() {
      return this.canWrite && !this.needsName && !this.seatsFull;
    },
  },
  async created() {
    try {
      await this.$store.dispatch("getUserPlan");
      await this.load();
    } catch (err) {
      this.notify(handleApiError(err, "Failed to load the team"), "error");
    } finally {
      this.$store.commit("setIsLoading", false);
    }
  },
  methods: {
    roleLabel(role) {
      return ROLE_LABELS[role] || role;
    },
    initials(member) {
      const words = (member.name || member.email || "")
        .split(/[\s@.]+/)
        .filter(Boolean);
      const first = (word) => (word ? Array.from(word)[0] : "");
      return (first(words[0]) + first(words[1]) || "?").toUpperCase();
    },
    formatDate(value) {
      return value ? new Date(value).toLocaleDateString() : "";
    },
    notify(content, color) {
      this.$store.commit("showSnackbarMessage", { content, color });
    },
    async load() {
      const res = await getTeamMembers();
      this.team = res.team;
      this.seats = res.seats;
      this.members = res.members;
      this.invitations = res.invitations;
      this.nameInput = res.team.name || "";
      this.loaded = true;
    },
    isSelf(member) {
      return member.userId === this.user?.uid;
    },
    canRemove(member) {
      return this.isAdmin && !member.accountHolder && !this.isSelf(member);
    },
    canLeave(member) {
      return !member.accountHolder && this.isSelf(member);
    },
    canChangeRole(member) {
      return this.isAdmin && !member.accountHolder;
    },
    /** an admin revokes any invitation, a member those they sent */
    canRevoke(invitation) {
      return (
        this.isAdmin ||
        (this.canWrite && invitation.invitedBy === this.user?.email)
      );
    },
    /** runs an action on a row, reloads the list; true when the action succeeded */
    async run(id, action, success) {
      this.busyId = id;
      try {
        try {
          await action();
        } catch (err) {
          this.notify(
            handleApiError(err, "Something went wrong, please try again"),
            "error",
          );
          return false;
        }
        this.notify(success, "success");
        try {
          await this.load();
        } catch (err) {
          this.notify(
            handleApiError(
              err,
              "Saved, but the list could not be refreshed: reload the page",
            ),
            "error",
          );
        }
        return true;
      } finally {
        // cleared after the reload too, so the row stays disabled while the list refreshes
        this.busyId = null;
      }
    },
    async saveName() {
      const name = this.nameInput.trim();
      if (!name) return;
      if (await this.run("name", () => renameTeam(name), "Team name saved")) {
        this.editingName = false;
      }
    },
    async invite() {
      const email = this.inviteEmail.trim();
      if (!EMAIL_PATTERN.test(email)) {
        this.notify("Please enter a valid email address.", "error");
        return;
      }
      if (
        await this.run(
          "invite",
          () => inviteToTeam(email, this.inviteRole),
          `Invitation sent to ${email}`,
        )
      ) {
        this.inviteEmail = "";
      }
    },
    resend(invitation) {
      return this.run(
        invitation.id,
        () => inviteToTeam(invitation.email, invitation.role),
        `Invitation sent again to ${invitation.email}`,
      );
    },
    revoke(invitation) {
      return this.run(
        invitation.id,
        () => revokeInvitation(invitation.id),
        "Invitation revoked",
      );
    },
    async changeRole(member, role) {
      const changed = await this.run(
        member.userId,
        () => changeMemberRole(member.userId, role),
        "Role changed",
      );
      // one's own role decides what the page shows (admin controls, canWrite): reload it
      if (changed && this.isSelf(member)) {
        await this.$store.dispatch("getUserPlan");
      }
      return changed;
    },
    remove(member) {
      if (
        !window.confirm(
          `Remove ${member.email || "this person"} from the team? Invitations they sent are cancelled too.`,
        )
      ) {
        return undefined;
      }
      return this.run(
        member.userId,
        () => removeMember(member.userId),
        "Removed from the team",
      );
    },
    async leave(member) {
      if (
        !window.confirm(
          "Leave this team? You will need a new invitation to come back.",
        )
      ) {
        return;
      }
      try {
        await removeMember(member.userId);
        // the own account, explicitly: not the next team on the list
        switchWorkspace(this.user.uid, this.user.uid);
      } catch (err) {
        this.notify(handleApiError(err, "Failed to leave the team"), "error");
      }
    },
  },
};
</script>

<style scoped>
/* ---------- canvas ---------------------------------------------------- */

.ln-page {
  min-height: 100%;
  background: var(--ln-canvas);
}

.ln-wrap {
  padding: 20px 16px 40px;
}

/* ---------- header ------------------------------------------------------ */

.tm-head {
  margin-bottom: 24px;
}

.ln-eyebrow {
  margin: 0 0 10px;
  font-family: var(--ln-mono);
  font-size: 10px;
  font-weight: 500;
  letter-spacing: 0.14em;
  text-transform: uppercase;
  color: var(--ln-muted);
}

.tm-title {
  margin: 0;
  font-family: sans-serif;
  font-size: 40px;
  font-weight: 400;
  line-height: 1.1;
  letter-spacing: -0.025em;
  color: var(--ln-ink);
}

.tm-lede {
  margin: 12px 0 0;
  max-width: 52ch;
  font-size: 14px;
  line-height: 1.6;
  color: var(--ln-muted);
}

/* ---------- layout: the lists lead on a wide screen, the actions lead on a phone */

.tm-layout {
  display: grid;
  grid-template-columns: minmax(0, 1fr);
  gap: 20px;
  align-items: start;
}

.tm-side,
.tm-main {
  display: flex;
  flex-direction: column;
  gap: 20px;
  min-width: 0;
}

/* ---------- cards ------------------------------------------------------- */

.tm-card {
  padding: 20px;
  border: 1px solid var(--ln-line);
  border-radius: var(--ln-radius);
  background: var(--ln-surface);
}

.tm-card--flush {
  padding: 0;
  overflow: hidden;
}

.tm-card__title {
  margin: 0 0 12px;
  min-width: 0;
  overflow-wrap: break-word;
  font-family: sans-serif;
  font-size: 16px;
  font-weight: 500;
  letter-spacing: -0.01em;
  color: var(--ln-ink);
}

.tm-card__head {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 20px 20px 16px;
}

.tm-card__head .tm-card__title {
  margin: 0;
}

.tm-count {
  padding: 1px 8px;
  border-radius: 10px;
  background: var(--ln-surface-2);
  border: 1px solid var(--ln-line);
  font-size: 12px;
  font-weight: 500;
  color: var(--ln-muted);
  font-variant-numeric: tabular-nums;
}

.tm-name-row {
  display: flex;
  align-items: center;
  gap: 8px;
}

.tm-name {
  margin: 0;
  min-width: 0;
  font-family: sans-serif;
  font-size: 22px;
  font-weight: 500;
  line-height: 1.25;
  letter-spacing: -0.015em;
  color: var(--ln-ink);
  overflow-wrap: anywhere;
}

.tm-text {
  margin: 0 0 12px;
  font-size: 14px;
  line-height: 1.6;
  color: var(--ln-ink-2);
}

.tm-text:last-child {
  margin-bottom: 0;
}

.tm-text a:not(.ln-btn) {
  color: var(--ln-accent);
}

.tm-note {
  margin: 12px 0 0;
  font-size: 12.5px;
  line-height: 1.6;
  color: var(--ln-muted);
}

.tm-note a {
  color: var(--ln-accent);
}

.tm-note--pad {
  margin: 0;
  padding: 16px 20px;
  border-top: 1px solid var(--ln-line);
}

/* ---------- seats ------------------------------------------------------- */

.tm-seats {
  margin-top: 16px;
  padding-top: 16px;
  border-top: 1px solid var(--ln-line);
}

.tm-seats__label {
  display: block;
  font-size: 14px;
  font-weight: 500;
  color: var(--ln-ink);
  font-variant-numeric: tabular-nums;
}

/* the fill is scaled, not resized: width is never animated or recomputed */
.tm-meter {
  height: 6px;
  margin-top: 12px;
  border-radius: 3px;
  background: var(--ln-line);
  overflow: hidden;
}

.tm-meter__fill {
  height: 100%;
  background: var(--ln-accent);
  transform-origin: left center;
}

.tm-meter--full .tm-meter__fill {
  background: var(--ln-yellow-fg);
}

/* ---------- forms ------------------------------------------------------- */

.tm-form {
  display: flex;
  flex-direction: column;
  gap: 12px;
  margin-top: 16px;
}

.tm-form .tm-text {
  margin: 0;
}

.tm-actions {
  display: flex;
  justify-content: flex-end;
  gap: 8px;
}

.tm-form .tm-actions .ln-btn {
  flex: 1 1 auto;
}

.tm-card :deep(.v-field__outline) {
  color: var(--ln-line);
  --v-field-border-opacity: 1;
}

.tm-card :deep(.v-field--focused .v-field__outline) {
  color: var(--ln-accent);
  --v-field-border-opacity: 1;
}

.tm-card :deep(.v-label) {
  font-family: sans-serif;
  font-size: 13px;
  color: var(--ln-muted);
}

.tm-card :deep(.v-field__input) {
  font-family: sans-serif;
  font-size: 14px;
  color: var(--ln-ink);
}

/* ---------- people lists ------------------------------------------------ */

.tm-list {
  margin: 0;
  padding: 0;
  list-style: none;
}

.tm-row {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 16px 20px;
  border-top: 1px solid var(--ln-line);
}

.tm-avatar {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  flex: 0 0 auto;
  width: 36px;
  height: 36px;
  border-radius: 50%;
  background: var(--ln-blue-bg);
  font-size: 13px;
  font-weight: 500;
  color: var(--ln-blue-fg);
}

.tm-avatar--pending {
  background: var(--ln-surface-2);
  border: 1px solid var(--ln-line);
  color: var(--ln-muted);
}

.tm-avatar--pending .v-icon {
  color: inherit;
}

.tm-row__text {
  min-width: 0;
  flex: 1 1 auto;
}

.tm-row__name {
  font-size: 14px;
  font-weight: 600;
  color: var(--ln-ink);
  overflow-wrap: anywhere;
}

.tm-row__sub {
  margin-top: 2px;
  font-size: 12.5px;
  color: var(--ln-muted);
  overflow-wrap: anywhere;
}

.tm-row__actions {
  display: flex;
  align-items: center;
  flex: 0 0 auto;
  gap: 4px;
}

.tm-role {
  padding: 0 8px;
  font-size: 13px;
  color: var(--ln-ink-2);
}

.tm-tag {
  margin-left: 8px;
  padding: 2px 8px;
  border: 1px solid var(--ln-line);
  border-radius: 10px;
  background: var(--ln-surface-2);
  font-size: 11.5px;
  font-weight: 500;
  color: var(--ln-ink-2);
  white-space: nowrap;
}

.tm-tag--warn {
  border-color: transparent;
  background: var(--ln-red-bg);
  color: var(--ln-red-fg);
}

/* ---------- buttons ----------------------------------------------------- */

.ln-btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  padding: 0 16px;
  height: 38px;
  border: 1px solid transparent;
  border-radius: var(--ln-radius-sm);
  background: transparent;
  font-family: sans-serif;
  font-size: 13px;
  font-weight: 500;
  line-height: 1;
  text-decoration: none;
  white-space: nowrap;
  cursor: pointer;
  transition:
    background-color 200ms var(--ln-ease),
    border-color 200ms var(--ln-ease),
    color 200ms var(--ln-ease),
    transform 120ms var(--ln-ease);
}

.ln-btn:active {
  transform: scale(0.98);
}

.ln-btn:disabled {
  opacity: 0.45;
  cursor: default;
  transform: none;
}

.ln-btn:focus-visible {
  outline: 2px solid var(--ln-ink);
  outline-offset: 2px;
}

.ln-btn__icon.v-icon {
  font-size: 15px;
  color: inherit;
}

.ln-btn__icon--right {
  margin-left: -2px;
}

.ln-btn--solid {
  background: var(--ln-accent);
  color: var(--ln-surface);
}

.ln-btn--solid:hover {
  background: var(--ln-accent-hover);
}

.ln-btn--quiet {
  border-color: var(--ln-line);
  background: var(--ln-surface);
  color: var(--ln-ink-2);
}

.ln-btn--quiet:hover {
  background: var(--ln-surface-2);
}

.ln-btn--sm {
  height: 30px;
  padding: 0 12px;
  font-size: 12px;
}

.ln-inline-link {
  font-weight: 500;
  color: var(--ln-ink-2);
  text-decoration: none;
  border-bottom: 1px solid var(--ln-line);
  transition:
    border-color 200ms var(--ln-ease),
    color 200ms var(--ln-ease);
}

.ln-inline-link:hover {
  color: var(--ln-ink);
  border-bottom-color: var(--ln-ink);
}

.ln-iconbtn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  flex: 0 0 auto;
  border: 0;
  border-radius: var(--ln-radius-sm);
  background: transparent;
  color: var(--ln-muted);
  cursor: pointer;
  transition:
    background-color 200ms var(--ln-ease),
    color 200ms var(--ln-ease);
}

.ln-iconbtn:hover {
  background: var(--ln-surface-2);
  color: var(--ln-ink);
}

.ln-iconbtn:disabled {
  opacity: 0.45;
  cursor: default;
}

.ln-iconbtn:focus-visible {
  outline: 2px solid var(--ln-ink);
  outline-offset: 1px;
}

.ln-iconbtn--danger:hover {
  background: var(--ln-red-bg);
  color: var(--ln-red-fg);
}

.ln-iconbtn .v-icon {
  font-size: 17px;
  color: inherit;
}

/* ---------- role menu: as the Setup menu of the Deep linking page ------- */

.ln-menu {
  min-width: 160px;
  padding: 4px;
  border: 1px solid var(--ln-line);
  border-radius: var(--ln-radius);
  background: var(--ln-surface);
  box-shadow: 0 10px 30px rgba(17, 17, 17, 0.1);
}

.ln-menu__item {
  display: flex;
  align-items: center;
  gap: 8px;
  width: 100%;
  padding: 8px 12px;
  border: 0;
  border-radius: var(--ln-radius-sm);
  background: transparent;
  font-family: sans-serif;
  font-size: 13px;
  color: var(--ln-ink-2);
  text-align: left;
  cursor: pointer;
}

.ln-menu__item:hover {
  background: var(--ln-surface-2);
}

.ln-menu__item:focus-visible {
  outline: 2px solid var(--ln-ink);
  outline-offset: -2px;
}

.ln-menu__item .v-icon {
  font-size: 16px;
  color: var(--ln-muted);
}

/* ---------- motion ------------------------------------------------------ */

@media (prefers-reduced-motion: reduce) {
  .ln-btn,
  .ln-iconbtn,
  .ln-inline-link {
    transition: none;
  }

  .ln-btn:active {
    transform: none;
  }
}

/* ---------- breakpoints ------------------------------------------------- */

/* a phone: the person's name and their actions get their own lines */
@media (max-width: 599px) {
  .tm-row {
    flex-wrap: wrap;
  }

  .tm-row__actions {
    margin-left: 48px;
    flex: 1 0 calc(100% - 48px);
  }
}

@media (min-width: 600px) {
  .ln-wrap {
    padding: 28px 20px 56px;
  }

  .tm-title {
    font-size: 52px;
  }

  .tm-card {
    padding: 24px;
  }

  .tm-card--flush {
    padding: 0;
  }

  .tm-card__head {
    padding: 20px 24px 16px;
  }

  .tm-row {
    padding: 16px 24px;
  }

  .tm-note--pad {
    padding: 16px 24px;
  }
}

@media (min-width: 960px) {
  .ln-wrap {
    padding: 36px 24px 64px;
  }
}

@media (min-width: 840px) {
  .tm-layout {
    grid-template-columns: minmax(0, 1fr) 340px;
    grid-template-areas: "main side";
  }

  .tm-main {
    grid-area: main;
  }

  .tm-side {
    grid-area: side;
  }
}
</style>
